import { useState, useEffect } from "react";
import { supabase } from "../../../../../../integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "../../../../../ui/card";
import { Skeleton } from "../../../../..//ui/skeleton";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";

interface PlatformChartProps {
  dateRange: string;
  outletIds?: string[];
  supabaseoutletIDs: string[];
}

export const PlatformChart = ({ dateRange, outletIds, supabaseoutletIDs }: PlatformChartProps) => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<any[]>([]);

  useEffect(() => {
    loadData();
  }, [dateRange, outletIds]);

  const loadData = async () => {
    setLoading(true);
    try {
      const daysAgo = dateRange === "7days" ? 7 : dateRange === "30days" ? 30 : 90;
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - daysAgo);

      let query = supabase
        .from("reviews")
        .select("platform, rating")
        .gte("posted_at", startDate.toISOString());

      const isAllSelected = !outletIds || outletIds.length === 0 || outletIds.includes("all");
      if (!isAllSelected && outletIds.length > 0) {
        query = query.in("outlet_id", outletIds);
      }else  {
        query = query.in("outlet_id", supabaseoutletIDs);
      }

      const { data: reviews, error } = await query;

      if (error) throw error;

      const platformData = reviews?.reduce((acc, r) => {
        const platform = r.platform || "unknown";
        if (!acc[platform]) {
          acc[platform] = { total: 0, sum: 0 };
        }
        acc[platform].total += 1;
        acc[platform].sum += r.rating || 0;
        return acc;
      }, {} as Record<string, { total: number; sum: number }>);

      const chartData = Object.entries(platformData || {}).map(([platform, stats]) => ({
        platform: platform.replace("_", " "),
        avgRating: stats.total > 0 ? Number((stats.sum / stats.total).toFixed(1)) : 0,
      }));

      setData(chartData);
    } catch (error) {
      console.error("Error loading platform data:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Platform Performance</CardTitle>
      </CardHeader>
      <CardContent>
        {loading ? (
          <Skeleton className="h-[250px] w-full" />
        ) : (
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={data}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="platform" />
              <YAxis domain={[0, 5]} />
              <Tooltip />
              <Bar dataKey="avgRating" fill="#8B5CF6" />
            </BarChart>
          </ResponsiveContainer>
        )}
      </CardContent>
    </Card>
  );
};
